import { useState } from "react";

import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  ORGANISATION_PRACITITIONER_LIST_QUERY,
  TEAM_MEMBER_FIELDS,
  UPDATE_PRACTITIONER_ROLE_MUTATION
} from "@/graphql/organisations/queries/practitioners";
import Accordion from "@/tpo/Accordion";
import Checkbox from "@/tpo/Checkbox";
import { Error } from "@/tpo/InputWrapper";
import Stack from "@/tpo/Stack";
import { useMutation } from "@apollo/client";

export function PractitionerPermissionsList({
  alignItems,
  inviteId,
  organisationPractitionerId,
  teamMemberId,
  listQueryVariables,
  roles
}) {
  const { organisation, organisationRoles } = useOrganisationContext();
  const [error, setError] = useState(null);

  const selectedRoleIds = roles.map(role => parseInt(role.id));

  const [updatePractitionerRoles] = useMutation(UPDATE_PRACTITIONER_ROLE_MUTATION, {
    update(cache, { data }) {
      const teamMember = data?.updatePractitionerRoleMutation?.teamMember;
      if (!teamMember || data?.updatePractitionerRoleMutation?.errors?.length) {
        return;
      }

      cache.writeFragment({
        id: cache.identify({
          __typename: "OrganisationTeamMemberType",
          id: teamMember.id
        }),
        fragment: TEAM_MEMBER_FIELDS,
        data: {
          __typename: "OrganisationTeamMemberType",
          ...teamMember
        }
      });

      if (!listQueryVariables) {
        return;
      }

      cache.updateQuery(
        {
          query: ORGANISATION_PRACITITIONER_LIST_QUERY,
          variables: listQueryVariables
        },
        existingData => {
          if (!existingData?.teamMembersPagination?.items) {
            return existingData;
          }

          return {
            ...existingData,
            teamMembersPagination: {
              ...existingData.teamMembersPagination,
              items: existingData.teamMembersPagination.items.map(item =>
                item.id === teamMember.id || item.id === teamMemberId
                  ? { ...item, roles: teamMember.roles }
                  : item
              )
            }
          };
        }
      );
    }
  });

  const handleRoleChange = (value, { target: { checked } }) => {
    const newRoles = new Set(selectedRoleIds);
    if (newRoles.has(value) && !checked) {
      newRoles.delete(value);
    } else if (!newRoles.has(value) && checked) {
      newRoles.add(value);
    }

    const input = {
      organisation: parseInt(organisation?.id),
      roles: Array.from(newRoles)
    };

    if (organisationPractitionerId) {
      input.organisationPractitionerId = organisationPractitionerId;
    } else {
      input.id = inviteId;
    }

    updatePractitionerRoles({
      variables: { input }
    })
      .then(resp => {
        if (resp?.data?.updatePractitionerRoleMutation?.errors?.length) {
          setError(resp.data.updatePractitionerRoleMutation.errors[0].messages[0]);
        } else {
          setError(null);
        }
      })
      .catch(err => {
        console.log("Error updating practitioner roles", err);
      });
  };

  const roleValues =
    organisationRoles?.map(({ id, name }) => ({
      label: name === "Admin" ? "Manager" : name,
      value: id
    })) ?? [];

  const controlProps = {
    [alignItems === "flex-start" ? "pr" : "pl"]: [15, 15, 20]
  };
  const panelProps = {
    [alignItems === "flex-start" ? "pr" : "pl"]: [20, 20, 40]
  };

  return (
    <Stack gap={10} width="100%" justifyContent="flex-end" flexDirection="row">
      <Accordion width="100%">
        <Accordion.Item value="practitionerPermissions">
          <Accordion.Control
            py={[2]}
            fontSize={10}
            fontFamily="gilroyBold"
            textTransform="uppercase"
            letterSpacing={2.8}
            justifyContent="flex-end"
            {...controlProps}
          >
            permissions
          </Accordion.Control>
          <Accordion.Panel
            pb={[2]}
            position="relative"
            textAlign={alignItems === "flex-start" ? "left" : "right"}
            {...panelProps}
          >
            <Stack
              flexDirection="row"
              justifyContent={["flex-start", "flex-end", "flex-end"]}
              flexWrap="wrap"
              gap={[20, 20, 40]}
              pt={2}
            >
              {roleValues.map(({ label, value }) => (
                <Checkbox
                  key={value}
                  value={value}
                  label={label}
                  checked={selectedRoleIds.includes(parseInt(value))}
                  onChange={evt => handleRoleChange(parseInt(value), evt)}
                />
              ))}
            </Stack>
            {!!error && <Error error={error} mt={20} />}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
